import { Vector3 } from 'three';
import { EventRegister } from '../../Utils';
import { PathType } from './Path';
import { CirclePathObject, FixedPathObject, LinePathObject } from './PathObjects';
/**
 * A widget is an object in the 3D world that moves along a certain path in
 * response to user gestures.
 *
 * Once an IWorldObject has been registered to a widget, never manually
 * set its position.
 *
 * Instead, update its position using setPath()
 */
export class Widget extends EventRegister {
    constructor(renderer, meshInstance, gestures, objects, path) {
        super();
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "meshInstance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "gestures", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "objects", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "visible", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "grabbed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "progress", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pathBehavior", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "gestureCallbacks", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.renderer = renderer;
        this.meshInstance = meshInstance;
        this.gestures = gestures;
        this.objects = objects;
        this.visible = false;
        this.grabbed = false;
        this.progress = 0;
        this.pathBehavior = null;
        if (path)
            this.setPath(path);
        this.gestureCallbacks = {
            tap: [],
            release: [],
            swipe: [],
            hover: [],
            keytap: [],
            keyrelease: [],
            zoom: [],
        };
        // Register event handlers
        this.handleGrab();
        this.handleRelease();
        this.handleMove();
        this.handleHover();
        this.handleLeave();
    }
    /**
     * Test if the widget is picked.
     *
     * @returns
     */
    isPicked(point, object) {
        const pickingId = this.meshInstance.getPickingId(object);
        const instance = this.meshInstance.getInstanceObject(object);
        const pick = this.renderer.pickScreenWorldSpaceSnapping(point);
        return pick.object === instance && pick.userData === pickingId;
    }
    /**
     * Create a corresponding path object to a definition.
     */
    getPathObject(path) {
        switch (path.type) {
            case PathType.Line: {
                const { origin, direction } = path;
                return new LinePathObject(this.renderer, new Vector3(origin.x, origin.y, origin.z), new Vector3(direction.x, direction.y, direction.z));
            }
            case PathType.Circle: {
                const { origin, center, normal, pivotCenter } = path;
                return new CirclePathObject(this.renderer, new Vector3(origin.x, origin.y, origin.z), new Vector3(center.x, center.y, center.z), new Vector3(normal.x, normal.y, normal.z), pivotCenter);
            }
            case PathType.Fixed: {
                const { origin } = path;
                return new FixedPathObject(new Vector3(origin.x, origin.y, origin.z));
            }
        }
    }
    /**
     * Handle grabbing the widget.
     */
    handleGrab() {
        const handler = (gesture) => {
            if (this.pathBehavior === null)
                return;
            if (!this.objects.find(object => this.isPicked(gesture.position, object)))
                return;
            this.grabbed = true;
            const position = this.getPosition();
            this.pathBehavior.onGrab();
            const callbacks = this.getCallbacks('grab');
            for (const callback of callbacks) {
                callback({ position });
            }
        };
        this.gestures.register('tap', handler);
        this.gestureCallbacks.tap.push(handler);
    }
    /**
     * Handle releasing the widget, only when it is already grabbed.
     */
    handleRelease() {
        const handler = () => {
            if (!this.grabbed || this.pathBehavior === null)
                return;
            this.grabbed = false;
            const position = this.getPosition();
            this.pathBehavior.onRelease();
            const callbacks = this.getCallbacks('release');
            for (const callback of callbacks) {
                callback({ position });
            }
        };
        this.gestures.register('release', handler);
        this.gestureCallbacks.release.push(handler);
    }
    /**
     * Handle moving the widget along the path.
     */
    handleMove() {
        const handler = (gesture) => {
            if (!this.grabbed || this.pathBehavior === null)
                return;
            const prevPosition = this.getPosition();
            this.pathBehavior.mapMotion(prevPosition.clone(), gesture);
            const position = this.pathBehavior.getWidgetPosition().clone();
            for (const object of this.objects) {
                object.position = position;
                this.meshInstance.refresh(object);
            }
            const progress = this.pathBehavior.getProgress();
            const deltaProgress = progress - this.progress;
            this.progress = progress;
            const callbacks = this.getCallbacks('move');
            for (const callback of callbacks) {
                callback({ position, prevPosition, progress, deltaProgress });
            }
        };
        this.gestures.register('swipe', handler);
        this.gestureCallbacks.swipe.push(handler);
    }
    /**
     * Handle hovering over a widget.
     */
    handleHover() {
        const handler = (gesture) => {
            if (!this.objects.find(object => this.isPicked(gesture.position, object)))
                return;
            const position = this.getPosition();
            const callbacks = this.getCallbacks('hover');
            for (const callback of callbacks) {
                callback({ position });
            }
        };
        this.gestures.register('hover', handler);
        this.gestureCallbacks.hover.push(handler);
    }
    /**
     * Handle pointer leaving a widget.
     */
    handleLeave() {
        const handler = (gesture) => {
            // Similar to hover handler, but terminate when the pointer is over a widget
            // It's essentially the inverse operation condition
            if (this.objects.find(object => this.isPicked(gesture.position, object)))
                return;
            const position = this.getPosition();
            const callbacks = this.getCallbacks('leave');
            for (const callback of callbacks) {
                callback({ position });
            }
        };
        this.gestures.register('hover', handler);
        this.gestureCallbacks.hover.push(handler);
    }
    /**
     * Check if the widget is currently grabbed.
     */
    isGrabbed() {
        return this.grabbed;
    }
    /**
     * Hide the widget.
     */
    hide() {
        this.visible = false;
        for (const object of this.objects) {
            this.meshInstance.removeMesh(object);
        }
    }
    /**
     * Show the widget.
     */
    show() {
        this.visible = true;
        for (const object of this.objects) {
            this.meshInstance.addMesh(object);
        }
    }
    /**
     * Check if the widget is shown.
     */
    isShown() {
        return this.visible;
    }
    /**
     * Get the world position of the widget.
     */
    getPosition() {
        const { x, y, z } = this.objects[0].position;
        return new Vector3(x, y, z);
    }
    /**
     * Set the color of the widget.
     */
    setColor(color) {
        for (const object of this.objects) {
            object.colors = [color];
            this.meshInstance.refresh(object);
        }
    }
    /**
     * Set the rotation of the widget.
     */
    setRotation(rotation) {
        for (const object of this.objects) {
            object.rotation = rotation;
            this.meshInstance.refresh(object);
        }
    }
    /**
     * Set the path of the widget.
     *
     * This will also move the widget to the origin of the path,
     * effectively resetting it.
     */
    setPath(path) {
        this.pathBehavior = this.getPathObject(path);
        this.pathBehavior.setFromProgress(this.progress);
        const position = this.pathBehavior.getWidgetPosition();
        for (const object of this.objects) {
            object.position = position;
            this.meshInstance.refresh(object);
        }
    }
    /**
     * Reset the progress value for this widget (= 0).
     */
    resetProgress() {
        this.progress = 0;
        if (this.pathBehavior) {
            this.pathBehavior.setFromProgress(this.progress);
            const position = this.pathBehavior.getWidgetPosition();
            for (const object of this.objects) {
                object.position = position;
                this.meshInstance.refresh(object);
            }
        }
    }
    /**
     * Completely destroy this widget if it is no longer being used.
     */
    destroy() {
        this.unregisterAll();
        this.hide();
        // Remove all gesture callbacks
        for (const key in this.gestureCallbacks) {
            const event = key;
            for (const callback of this.gestureCallbacks[event]) {
                this.gestures.unregister(event, callback);
            }
        }
    }
}
